import { useState, type FormEvent } from "react";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Match } from "effect";
import {
  PaymentDeviceSchema,
  PaymentDevicesResponseSchema,
} from "../../../packages/common/src/schemas/payment-devices.ts";
import { ApiError, type StaffApi } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectMutation, runEffectQuery } from "#effect-query.ts";
import { useLanguage } from "#i18n.tsx";

/** Stable query key for one merchant's observed payment devices. */
export const paymentDevicesQueryKey = (merchantId: string) =>
  ["merchants", merchantId, "payment-devices"] as const;

/** Shared device-list query used by kitchen filters and administrator settings. */
export const paymentDevicesQueryOptions = (
  api: StaffApi,
  merchantId: string | undefined,
) =>
  queryOptions<typeof PaymentDevicesResponseSchema.Type, ApiError>({
    queryKey: paymentDevicesQueryKey(merchantId ?? "unavailable"),
    enabled: merchantId !== undefined,
    retry: false,
    queryFn: ({ signal }) =>
      runEffectQuery(
        api.requestEffect({
          path: `/merchants/${encodeURIComponent(merchantId ?? "")}/payment-devices`,
          schema: PaymentDevicesResponseSchema,
          signal,
        }),
        signal,
      ),
  });

/** Administrator editor for names shown in kitchen payment-device filters. */
export function PaymentDeviceSettings({
  merchantId,
}: {
  readonly merchantId: string;
}) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const devicesQuery = useQuery(paymentDevicesQueryOptions(api, merchantId));
  const [editingId, setEditingId] = useState<string>();
  const [name, setName] = useState("");
  const rename = useMutation({
    retry: false,
    mutationFn: ({
      deviceId,
      nextName,
    }: {
      readonly deviceId: string;
      readonly nextName: string;
    }) =>
      runEffectMutation(
        api.requestEffect({
          path: `/merchants/${encodeURIComponent(merchantId)}/payment-devices/${encodeURIComponent(deviceId)}`,
          method: "PUT",
          body: { name: nextName },
          schema: PaymentDeviceSchema,
        }),
      ),
    onSuccess: () => {
      setEditingId(undefined);
      setName("");
      void queryClient.invalidateQueries({
        queryKey: paymentDevicesQueryKey(merchantId),
      });
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextName = name.trim();
    if (editingId && nextName)
      rename.mutate({ deviceId: editingId, nextName });
  };

  return (
    <section className="settings-card">
      <h2>{t("paymentDevices.title")}</h2>
      <p>{t("paymentDevices.description")}</p>
      {Match.value(devicesQuery).pipe(
        Match.when({ status: "pending" }, () => (
          <p role="status">{t("paymentDevices.loading")}</p>
        )),
        Match.when({ status: "error" }, ({ error }) =>
          Match.valueTags(error, {
            ApiError: (failure) => (
              <p className="alert" role="alert">
                {failure.message}
              </p>
            ),
          }),
        ),
        Match.when({ status: "success" }, ({ data }) =>
          data.devices.length === 0 ? (
            <p className="muted">
              {t("paymentDevices.none")}
            </p>
          ) : (
            <ul className="preset-settings-list">
              {data.devices.map((device) => (
                <li key={device.device_id}>
                  {editingId === device.device_id ? (
                    <form className="device-name-form" onSubmit={submit}>
                      <label className="field">
                        {t("paymentDevices.name")}
                        <input
                          autoFocus
                          required
                          maxLength={60}
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                        />
                      </label>
                      <div className="button-row">
                        <button className="primary" disabled={rename.isPending}>
                          {rename.isPending
                            ? t("settings.saving")
                            : t("paymentDevices.save")}
                        </button>
                        <button
                          type="button"
                          className="secondary"
                          disabled={rename.isPending}
                          onClick={() => setEditingId(undefined)}
                        >
                          {t("common.cancel")}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <span>
                        <strong>{device.name}</strong>
                        <small>
                          {t("paymentDevices.squareId", {
                            id: device.device_id.slice(-8),
                          })}
                        </small>
                      </span>
                      <button
                        className="secondary"
                        onClick={() => {
                          setEditingId(device.device_id);
                          setName(device.name);
                          rename.reset();
                        }}
                      >
                        {t("paymentDevices.rename")}
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ),
        ),
        Match.exhaustive,
      )}
      {rename.error && (
        <p className="alert" role="alert">{rename.error.message}</p>
      )}
    </section>
  );
}
